import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { Project, Customer, Tag, Domain } from '../types';
import { supabase } from '../lib/supabase';

interface FileUploadProps {
  onDataLoaded: (data: Project[]) => void;
}

interface CSVRow {
  projectName: string;
  customerName: string;
  customerDomain: string;
  tags: string[];
}

export function FileUpload({ onDataLoaded }: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());

        // Parse CSV data
        const rows: CSVRow[] = lines
          .slice(1)
          .filter(line => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return false;
            if (trimmedLine.replace(/,/g, '').trim().length === 0) return false;
            return true;
          })
          .map(line => {
            const values = line.split(',').map(v => v.trim());
            const projectNameIndex = headers.findIndex(h => h.toLowerCase() === 'projectname');
            const customerNameIndex = headers.findIndex(h => h.toLowerCase() === 'customername');
            const customerDomainIndex = headers.findIndex(h => h.toLowerCase() === 'customerdomain');
            
            const tagNames = headers
              .map((header, index) => ({ header: header.toLowerCase(), index }))
              .filter(({ header }) => header.startsWith('tag'))
              .map(({ index }) => values[index])
              .filter(tag => tag && tag.trim());

            return {
              projectName: values[projectNameIndex],
              customerName: values[customerNameIndex],
              customerDomain: values[customerDomainIndex],
              tags: tagNames
            };
          })
          .filter(row => row.projectName && row.customerName && row.customerDomain);

        if (rows.length === 0) {
          throw new Error('No valid projects found in the CSV file');
        }

        try {
          // Create unique sets
          const uniqueCustomers = new Set(rows.map(row => row.customerName));
          const uniqueDomains = new Set(rows.map(row => row.customerDomain));
          const uniqueTags = new Set(rows.flatMap(row => row.tags));

          // Create all unique customers first
          const customerMap = new Map<string, string>(); // Map of customer name to id
          for (const customerName of uniqueCustomers) {
            // Check if customer exists
            const { data: existingCustomers } = await supabase
              .from('customers')
              .select('id, name')
              .eq('name', customerName);

            if (existingCustomers && existingCustomers.length > 0) {
              customerMap.set(customerName, existingCustomers[0].id);
            } else {
              const { data: newCustomer, error: createCustomerError } = await supabase
                .from('customers')
                .insert({ name: customerName })
                .select()
                .single();

              if (createCustomerError) throw createCustomerError;
              customerMap.set(customerName, newCustomer.id);
            }
          }

          // Create all unique domains
          const domainMap = new Map<string, string>(); // Map of domain name to id
          for (const domainName of uniqueDomains) {
            // Check if domain exists
            const { data: existingDomains } = await supabase
              .from('domains')
              .select('id, name')
              .eq('name', domainName);

            if (existingDomains && existingDomains.length > 0) {
              domainMap.set(domainName, existingDomains[0].id);
            } else {
              const { data: newDomain, error: createDomainError } = await supabase
                .from('domains')
                .insert({ name: domainName })
                .select()
                .single();

              if (createDomainError) throw createDomainError;
              domainMap.set(domainName, newDomain.id);
            }
          }

          // Create all unique tags
          const tagMap = new Map<string, string>(); // Map of tag name to id
          for (const tagName of uniqueTags) {
            // Check if tag exists
            const { data: existingTags } = await supabase
              .from('tags')
              .select('id, name')
              .eq('name', tagName);

            if (existingTags && existingTags.length > 0) {
              tagMap.set(tagName, existingTags[0].id);
            } else {
              const { data: newTag, error: createTagError } = await supabase
                .from('tags')
                .insert({ name: tagName })
                .select()
                .single();

              if (createTagError) throw createTagError;
              tagMap.set(tagName, newTag.id);
            }
          }

          // Create projects and their relationships
          for (const row of rows) {
            const customerId = customerMap.get(row.customerName);
            const domainId = domainMap.get(row.customerDomain);

            if (!customerId) {
              throw new Error(`Customer not found: ${row.customerName}`);
            }
            if (!domainId) {
              throw new Error(`Domain not found: ${row.customerDomain}`);
            }

            // Create project
            const { data: project, error: projectError } = await supabase
              .from('projects')
              .insert({
                project_name: row.projectName,
                customer_id: customerId,
                domain_id: domainId
              })
              .select()
              .single();

            if (projectError) throw projectError;

            // Create project-tag relationships
            if (row.tags.length > 0) {
              const tagIds = row.tags
                .map(tagName => tagMap.get(tagName))
                .filter((id): id is string => id !== undefined);

              if (tagIds.length > 0) {
                // Check for existing project-tag relationships and only insert new ones
                for (const tagId of tagIds) {
                  const { data: existingRelation } = await supabase
                    .from('project_tags')
                    .select('*')
                    .eq('project_id', project.id)
                    .eq('tag_id', tagId);

                  if (!existingRelation || existingRelation.length === 0) {
                    const { error: tagRelationError } = await supabase
                      .from('project_tags')
                      .insert({
                        project_id: project.id,
                        tag_id: tagId
                      });

                    if (tagRelationError) throw tagRelationError;
                  }
                }
              }
            }
          }

          // Reload projects to get the latest data
          onDataLoaded([]);
        } catch (err) {
          console.error('Error processing data:', err);
          throw new Error('Error processing data. Please check the console for details.');
        }
      };

      reader.readAsText(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while uploading the file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
      <label className="cursor-pointer flex flex-col items-center">
        <Upload className={`w-12 h-12 ${isUploading ? 'text-gray-300' : 'text-gray-400'} mb-2`} />
        <span className="text-lg font-medium text-gray-600">
          {isUploading ? 'Uploading...' : 'Upload CSV File'}
        </span>
        <span className="text-sm text-gray-500 mt-1">Click or drag and drop</span>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          disabled={isUploading}
          className="hidden"
        />
      </label>
      {error && (
        <div className="mt-4 text-sm text-red-600">
          {error}
        </div>
      )}
      <div className="mt-4 text-sm text-gray-500">
        <p>Expected CSV format:</p>
        <p>projectName, customerName, customerDomain, tag1, tag2, tag3, ...</p>
      </div>
    </div>
  );
}